import {
	TEST_ELEMENT_IDS,
	UIElementIdentifier,
} from '@dealstack/frontend/shared/helper/UIElementIdentifier/UIElementIdentifier';
import { Flex } from '@mantine/core';
import { memo, useMemo, useState } from 'react';
import styled from 'styled-components';

import {
	SideTabItemV2,
	SideTabItemV2State,
} from '@dealstack/frontend/shared/components/list/SideTabItemV2/SideTabItemV2';
import {
	SubListItemV2,
	SubListItemV2State,
} from '@dealstack/frontend/shared/components/list/SubListItemV2/SubListItemV2';
import type { PrimarySideListV2Props, SideListItem, SubListItem } from './PrimarySideListV2.types';

const PrimarySideListV2 = ({
	sideListItems,
	activeKey,
	onActiveChange,
}: PrimarySideListV2Props) => {
	const [expandedMainStepsKeys, setExpandedMainStepsKeys] = useState<(number | string)[]>([]);

	const mainStepsState = useMemo((): Record<string | number, SideTabItemV2State> => {
		const activeMainStepIndex = sideListItems.findIndex(
			(item) =>
				item.key === activeKey ||
				item.subItems?.some((subItem) => subItem.key === activeKey)
		);

		return sideListItems?.reduce((acc, { key, state, subItems }, index) => {
			if (state) {
				return { ...acc, [key]: state };
			}

			const isActive = key === activeKey;
			const isSubItemActive = subItems?.some((subItem) => subItem.key === activeKey);
			if (isActive || isSubItemActive) {
				return { ...acc, [key]: SideTabItemV2State.active };
			}

			const isAlreadyVisited = index < activeMainStepIndex;
			if (isAlreadyVisited) {
				return { ...acc, [key]: SideTabItemV2State.completed };
			}

			return { ...acc, [key]: SideTabItemV2State.default };
		}, {});
	}, [activeKey, sideListItems]);

	const subStepsState = useMemo((): Record<string | number, SubListItemV2State> => {
		const inlinedSteps = sideListItems?.reduce<(SubListItem | SideListItem)[]>(
			(acc, current) => {
				return [...acc, current, ...(current.subItems || [])];
			},
			[]
		);
		const activeStepIndex = inlinedSteps.findIndex((item) => item.key === activeKey);

		return inlinedSteps?.reduce((acc, item, index) => {
			if ('subItems' in item) {
				return acc;
			}
			if (item.state) {
				return { ...acc, [item.key]: item.state };
			}

			const isActive = item.key === activeKey;
			if (isActive) {
				return { ...acc, [item.key]: SubListItemV2State.active };
			}

			const isAlreadyVisited = index < activeStepIndex;
			if (isAlreadyVisited) {
				return { ...acc, [item.key]: SubListItemV2State.completed };
			}

			return { ...acc, [item.key]: SubListItemV2State.default };
		}, {});
	}, [activeKey, sideListItems]);

	const tree = useMemo(() => {
		return sideListItems?.map((entry, index) => {
			const state = mainStepsState[entry.key];
			const isExpanded = expandedMainStepsKeys.some((stepKey) => stepKey === entry.key);
			const collapseSublist = isExpanded ? false : state !== 'active';

			const subList = collapseSublist
				? null
				: entry?.subItems?.map((subEntry, subItemIndex) => {
						const state = subStepsState[subEntry.key];
						if (!state) {
							return;
						}
						return (
							<UIElementIdentifier
								identifier={TEST_ELEMENT_IDS.SUB_LIST_TAB}
								key={subEntry.key}
								index={index}
							>
								<SubListItemV2
									listNumber={subItemIndex}
									title={subEntry.title}
									state={state}
									onClick={
										subEntry.allowNavigation && onActiveChange
											? () => onActiveChange(subEntry.key)
											: undefined
									}
								/>
							</UIElementIdentifier>
						);
					});

			if (isExpanded && !entry.allowExpandByClick) {
				setExpandedMainStepsKeys(
					expandedMainStepsKeys.filter((stepKey) => stepKey !== entry.key)
				);
			}

			const onClick = () => {
				if (entry.allowNavigation && onActiveChange) {
					onActiveChange(entry.key);
				}
				if (!entry.allowExpandByClick) {
					return;
				}
				if (isExpanded) {
					setExpandedMainStepsKeys(
						expandedMainStepsKeys.filter((stepKey) => stepKey !== entry.key)
					);
				} else {
					setExpandedMainStepsKeys([...expandedMainStepsKeys, entry.key]);
				}
			};
			if (!state) {
				return;
			}
			return (
				<div key={entry.title}>
					<UIElementIdentifier identifier={TEST_ELEMENT_IDS.SIDEBAR_TAB} index={index}>
						<SideTabItemV2
							listNumber={index}
							title={entry.title}
							state={state === 'active' ? state : entry?.state || state}
							onClick={
								entry.allowNavigation || entry.allowExpandByClick
									? onClick
									: undefined
							}
						/>
					</UIElementIdentifier>
					{!!subList && <SubList>{subList}</SubList>}
				</div>
			);
		});
	}, [
		expandedMainStepsKeys,
		activeKey,
		mainStepsState,
		subStepsState,
		sideListItems,
		onActiveChange,
	]);

	return (
		<Flex direction="column" pos="relative" gap="0.5rem" style={{ zIndex: 0 }}>
			{tree}
		</Flex>
	);
};

const SubList = styled.div`
	display: flex;
	flex-direction: column;
	gap: 0.5rem;
	margin: 0.6rem 1rem 0.1rem 2.5rem;
`;
const _PrimarySideListV2Export = memo(PrimarySideListV2);

export { _PrimarySideListV2Export as PrimarySideListV2 };
